import React, { useState, useEffect, useMemo } from 'react';
import { X, Search, Plus, Building2, User } from 'lucide-react';
import { $api } from '../../../api';
import { useQueryClient } from '@tanstack/react-query';
import AddEntityModal from '../../Entities/AddEntityModal';
import ErrorMessage from '../../ErrorMessage';

interface Relation {
  id: string;
  parent_entity_id: string;
  child_entity_id: string;
  relation_kind: 'shareholder' | 'executive' | 'director' | 'ubo' | 'other';
  percentage?: number;
  entity_type: 'company' | 'person';
  entity_name: string;
}

interface Entity {
  id: string;
  name: string;
  kind: 'company' | 'person';
  email?: string;
}

interface AddRelationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: () => void;
  editingRelation?: Relation | null;
  organizationId: string;
  entityId: string;
  currentEntityName: string;
  relationDirection: 'incoming' | 'outgoing';
}

type RelationKind = 'shareholder' | 'executive' | 'director' | 'ubo' | 'other';

interface SelectedRelationKind {
  kind: RelationKind;
  percentage?: string;
  relationId?: string; // For editing: ID of existing relation
}

interface RelationModalLayoutProps {
  title: string;
  onClose: () => void;
  onSubmit: (e: React.FormEvent) => void;
  isLoading: boolean;
  error?: unknown;
  relationDirection: 'incoming' | 'outgoing';
  currentEntityName: string;
  selectedRelationKinds: SelectedRelationKind[];
  onRelationKindToggle: (kind: RelationKind) => void;
  onPercentageChange: (kind: RelationKind, percentage: string) => void;
  searchTerm: string;
  onSearchTermChange: (value: string) => void;
  selectedEntity: Entity | null;
  onSelectedEntityChange: (entity: Entity | null) => void;
  isEditMode: boolean;
  entitiesQuery?: {
    isLoading: boolean;
    data?: { entities?: Entity[] };
  };
  filteredEntities: Entity[];
  onCreateEntityClick: (type: 'entreprise' | 'personne') => void;
  canSubmit: boolean;
  submitButtonText: string;
}

const relationKinds = [
  { id: 'shareholder' as const, label: 'Actionnaire' },
  { id: 'director' as const, label: 'Directeur' },
  { id: 'executive' as const, label: 'Dirigeant' },
  { id: 'ubo' as const, label: 'UBO (Bénéficiaire effectif)' },
  { id: 'other' as const, label: 'Autre' }
];

// Helper function to generate relation description text
const getRelationDescription = (
  relationDirection: 'incoming' | 'outgoing',
  selectedRelationKinds: SelectedRelationKind[],
  currentEntityName: string
): { title: string; description: string } => {
  if (relationDirection === 'incoming') {
    return {
      title: 'Ajouter un actionnaire ou dirigeant',
      description: `L'entité que vous sélectionnez sera <strong>actionnaire, directeur, dirigeant ou UBO</strong> de <strong>${currentEntityName}</strong>`
    };
  }

  // For outgoing relations, generate description based on selected kinds
  if (selectedRelationKinds.length === 0) {
    return {
      title: 'Ajouter une participation ou filiale',
      description: `<strong>${currentEntityName}</strong> aura une relation avec l'entité que vous sélectionnez`
    };
  }

  const selectedLabels = selectedRelationKinds.map(kind => {
    const relationKind = relationKinds.find(k => k.id === kind.kind);
    return relationKind?.label.toLowerCase() || kind.kind;
  });

  let relationText: string;
  if (selectedLabels.length === 1) {
    relationText = `<strong>${selectedLabels[0]}</strong>`;
  } else if (selectedLabels.length === 2) {
    relationText = `<strong>${selectedLabels[0]}</strong> et <strong>${selectedLabels[1]}</strong>`;
  } else {
    const lastLabel = selectedLabels[selectedLabels.length - 1];
    const otherLabels = selectedLabels.slice(0, -1).join('</strong>, <strong>');
    relationText = `<strong>${otherLabels}</strong> et <strong>${lastLabel}</strong>`;
  }

  return {
    title: 'Ajouter une participation ou filiale',
    description: `<strong>${currentEntityName}</strong> sera ${relationText} de l'entité que vous sélectionnez`
  };
};

const RelationModalLayout: React.FC<RelationModalLayoutProps> = ({
  title,
  onClose,
  onSubmit,
  isLoading,
  error,
  relationDirection,
  currentEntityName,
  selectedRelationKinds,
  onRelationKindToggle,
  onPercentageChange,
  searchTerm,
  onSearchTermChange,
  selectedEntity,
  onSelectedEntityChange,
  isEditMode,
  entitiesQuery,
  filteredEntities,
  onCreateEntityClick,
  canSubmit,
  submitButtonText,
}) => {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">{title}</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        <form onSubmit={onSubmit} className="p-6 space-y-6">
          {error ? <ErrorMessage error={error} /> : null}

          {/* Direction explanation */}
          {(() => {
            const relationDesc = getRelationDescription(relationDirection, selectedRelationKinds, currentEntityName);
            return (
              <div className={`p-4 rounded-lg border-2 ${
                relationDirection === 'incoming' 
                  ? 'bg-blue-50 border-blue-200' 
                  : 'bg-green-50 border-green-200'
              }`}>
                <div className="flex items-start space-x-2">
                  {relationDirection === 'incoming' ? (
                    <>
                      <User className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
                      <div className="flex-1">
                        <p className="text-sm font-medium text-blue-900 mb-1">
                          {relationDesc.title}
                        </p>
                        <p className="text-xs text-blue-700" dangerouslySetInnerHTML={{ __html: relationDesc.description }} />
                      </div>
                    </>
                  ) : (
                    <>
                      <Building2 className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
                      <div className="flex-1">
                        <p className="text-sm font-medium text-green-900 mb-1">
                          {relationDesc.title}
                        </p>
                        <p className="text-xs text-green-700" dangerouslySetInnerHTML={{ __html: relationDesc.description }} />
                      </div>
                    </>
                  )}
                </div>
              </div>
            );
          })()}

          {/* Nature de la relation */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Nature de la relation * (vous pouvez sélectionner plusieurs types)
            </label>
            <div className="space-y-3">
              {relationKinds.map((kind) => {
                const isSelected = selectedRelationKinds.some(r => r.kind === kind.id);
                const selectedKind = selectedRelationKinds.find(r => r.kind === kind.id);
                const needsPercentage = kind.id === 'shareholder' || kind.id === 'ubo';
                
                return (
                  <div key={kind.id} className="border border-gray-200 rounded-lg p-3">
                    <label className="flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => onRelationKindToggle(kind.id)}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <span className="ml-2 text-sm font-medium text-gray-700">{kind.label}</span>
                    </label>
                    
                    {/* Pourcentage input for shareholder and UBO */}
                    {isSelected && needsPercentage && (
                      <div className="mt-2 ml-6">
                        <label className="block text-xs text-gray-600 mb-1">
                          Pourcentage de participation (%)
                        </label>
                        <input
                          type="number"
                          min="0"
                          max="100"
                          step="0.01"
                          value={selectedKind?.percentage || ''}
                          onChange={(e) => onPercentageChange(kind.id, e.target.value)}
                          placeholder="Ex: 25.5"
                          className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            {selectedRelationKinds.length === 0 && isEditMode && (
              <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-sm font-medium text-red-900">
                  Aucune relation sélectionnée
                </p>
                <p className="text-xs text-red-700 mt-1">
                  En soumettant, toutes les relations entre ces deux entités seront supprimées.
                </p>
              </div>
            )}
            {selectedRelationKinds.length === 0 && !isEditMode && (
              <p className="text-sm text-gray-500 mt-2">Sélectionnez au moins un type de relation</p>
            )}
            {selectedRelationKinds.length > 0 && (
              <div className={`mt-3 p-3 border rounded-lg ${
                isEditMode 
                  ? 'bg-green-50 border-green-200' 
                  : 'bg-blue-50 border-blue-200'
              }`}>
                <p className={`text-sm font-medium mb-1 ${
                  isEditMode ? 'text-green-900' : 'text-blue-900'
                }`}>
                  {isEditMode 
                    ? `${selectedRelationKinds.length} relation${selectedRelationKinds.length > 1 ? 's' : ''} ser${selectedRelationKinds.length > 1 ? 'ont' : 'a'} modifiée${selectedRelationKinds.length > 1 ? 's' : ''} :`
                    : `${selectedRelationKinds.length} relation${selectedRelationKinds.length > 1 ? 's' : ''} ser${selectedRelationKinds.length > 1 ? 'ont' : 'a'} créée${selectedRelationKinds.length > 1 ? 's' : ''} :`
                  }
                </p>
                <ul className={`text-xs space-y-1 ${
                  isEditMode ? 'text-green-800' : 'text-blue-800'
                }`}>
                  {selectedRelationKinds.map((selectedKind) => {
                    const kindLabel = relationKinds.find(k => k.id === selectedKind.kind)?.label || selectedKind.kind;
                    return (
                      <li key={selectedKind.kind} className="flex items-center justify-between">
                        <span>• {kindLabel}</span>
                        {selectedKind.percentage && (
                          <span className="font-medium">{selectedKind.percentage}%</span>
                        )}
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}
          </div>

          {/* Recherche d'entité */}
          {!isEditMode && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Rechercher une entité *
              </label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => {
                    const newSearchTerm = e.target.value;
                    onSearchTermChange(newSearchTerm);
                    // Clear selected entity when user modifies the search field
                    if (selectedEntity && newSearchTerm !== selectedEntity.name) {
                      onSelectedEntityChange(null);
                    }
                  }}
                  placeholder="Nom ou email (minimum 2 caractères)..."
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
          )}

          {/* Entité (read-only in edit mode) */}
          {isEditMode && selectedEntity && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Entité
              </label>
              <div className="p-4 bg-gray-50 border border-gray-300 rounded-lg">
                <div className="flex items-center space-x-3">
                  <div className="h-10 w-10 bg-gray-200 rounded-full flex items-center justify-center">
                    {selectedEntity.kind === 'company' ? (
                      <Building2 className="h-5 w-5 text-gray-600" />
                    ) : (
                      <User className="h-5 w-5 text-gray-600" />
                    )}
                  </div>
                  <div className="flex-1">
                    <h4 className="font-medium text-gray-900">{selectedEntity.name}</h4>
                    {selectedEntity.email && (
                      <p className="text-sm text-gray-600">{selectedEntity.email}</p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Entité sélectionnée (only show in create mode) */}
          {selectedEntity && !isEditMode && (
            <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex items-center space-x-3">
                <div className="h-10 w-10 bg-blue-100 rounded-full flex items-center justify-center">
                  {selectedEntity.kind === 'company' ? (
                    <Building2 className="h-5 w-5 text-blue-600" />
                  ) : (
                    <User className="h-5 w-5 text-blue-600" />
                  )}
                </div>
                <div className="flex-1">
                  <h4 className="font-medium text-gray-900">{selectedEntity.name}</h4>
                  {selectedEntity.email && (
                    <p className="text-sm text-gray-600">{selectedEntity.email}</p>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => onSelectedEntityChange(null)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>
          )}

          {/* Liste des entités trouvées */}
          {!isEditMode && searchTerm.length >= 2 && !selectedEntity && (
            <div className="border border-gray-200 rounded-lg max-h-60 overflow-y-auto">
              {entitiesQuery?.isLoading ? (
                <div className="p-4 text-center">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mx-auto"></div>
                  <p className="text-gray-500 mt-2">Recherche en cours...</p>
                </div>
              ) : filteredEntities.length === 0 ? (
                <div className="p-4 text-center">
                  <p className="text-gray-500 mb-3">Aucune entité trouvée</p>
                  <div className="space-x-2">
                    <button
                      type="button"
                      onClick={() => onCreateEntityClick('personne')}
                      className="bg-blue-600 text-white px-3 py-1 rounded text-sm hover:bg-blue-700 transition-colors"
                    >
                      <Plus className="h-3 w-3 inline mr-1" />
                      Créer une personne
                    </button>
                    <button
                      type="button"
                      onClick={() => onCreateEntityClick('entreprise')}
                      className="bg-green-600 text-white px-3 py-1 rounded text-sm hover:bg-green-700 transition-colors"
                    >
                      <Plus className="h-3 w-3 inline mr-1" />
                      Créer une entreprise
                    </button>
                  </div>
                </div>
              ) : (
                <div className="divide-y divide-gray-200">
                  {filteredEntities.map((entity) => (
                    <button
                      key={entity.id}
                      type="button"
                      onClick={() => onSelectedEntityChange(entity)}
                      className="w-full p-4 text-left hover:bg-gray-50 transition-colors"
                    >
                      <div className="flex items-center space-x-3">
                        <div className="h-8 w-8 bg-blue-100 rounded-full flex items-center justify-center">
                          {entity.kind === 'company' ? (
                            <Building2 className="h-4 w-4 text-blue-600" />
                          ) : (
                            <User className="h-4 w-4 text-blue-600" />
                          )}
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">{entity.name}</p>
                          {entity.email && (
                            <p className="text-sm text-gray-600">{entity.email}</p>
                          )}
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              disabled={isLoading}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50"
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={!canSubmit || isLoading}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? 'Enregistrement...' : submitButtonText}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const AddRelationModalComponent: React.FC<Omit<AddRelationModalProps, 'editingRelation'> & { editingRelation: null }> = ({ 
  isOpen, 
  onClose, 
  onSubmit,
  organizationId,
  entityId,
  currentEntityName,
  relationDirection
}) => {
  const queryClient = useQueryClient();
  const [selectedRelationKinds, setSelectedRelationKinds] = useState<SelectedRelationKind[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedEntity, setSelectedEntity] = useState<Entity | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createEntityType, setCreateEntityType] = useState<'entreprise' | 'personne'>('personne');

  // Fetch entities for search
  const entitiesQuery = $api.useQuery(
    'get',
    '/api/organization/{organization_id}/entities',
    {
      params: {
        path: { organization_id: organizationId },
        query: {
          search: searchTerm || undefined,
          limit: '50',
        },
      },
    },
    {
      enabled: isOpen && !!organizationId && searchTerm.length >= 2,
    }
  );

  // Create relation mutation
  const createRelationMutation = $api.useMutation(
    'post',
    '/api/organization/{organization_id}/entities/{id}/relations'
  );

  // Reset form when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setSelectedRelationKinds([]);
      setSearchTerm('');
      setSelectedEntity(null);
    }
  }, [isOpen]);

  const entities = entitiesQuery.data?.entities || [];
  const filteredEntities = entities.filter(entity => 
    entity.id !== entityId && // Don't allow self-relation
    (!searchTerm || entity.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
     (entity.email && entity.email.toLowerCase().includes(searchTerm.toLowerCase())))
  );

  const handleRelationKindToggle = (kind: RelationKind) => {
    setSelectedRelationKinds(prev => {
      const existing = prev.find(r => r.kind === kind);
      if (existing) {
        return prev.filter(r => r.kind !== kind);
      } else {
        return [...prev, { kind, percentage: '' }];
      }
    });
  };

  const handlePercentageChange = (kind: RelationKind, percentage: string) => {
    setSelectedRelationKinds(prev =>
      prev.map(r => r.kind === kind ? { ...r, percentage } : r)
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!selectedEntity) {
      alert('Veuillez sélectionner une entité');
      return;
    }

    if (selectedRelationKinds.length === 0) {
      alert('Veuillez sélectionner au moins un type de relation');
      return;
    }

    // Create multiple relations (one for each selected kind)
    const parentEntityId = relationDirection === 'incoming' ? selectedEntity.id : entityId;
    const childEntityId = relationDirection === 'incoming' ? entityId : selectedEntity.id;

    // Create all relations sequentially to avoid race conditions
    try {
      for (const selectedKind of selectedRelationKinds) {
        await createRelationMutation.mutateAsync({
          params: {
            path: {
              organization_id: organizationId,
              id: parentEntityId,
            },
          },
          body: {
            child_entity_id: childEntityId,
            relation_kind: selectedKind.kind,
            percentage: selectedKind.percentage ? parseFloat(selectedKind.percentage) : undefined,
          },
        });
      }
      // All relations created successfully
      onSubmit();
      onClose();
    } catch {
      // Error is handled by the mutation's error state
    }
  };

  const isLoading = createRelationMutation.isPending;
  const error = createRelationMutation.error;

  if (!isOpen) return null;

  return (
    <>
      <RelationModalLayout
        title="Ajouter une relation"
        onClose={onClose}
        onSubmit={handleSubmit}
        isLoading={isLoading}
        error={error}
        relationDirection={relationDirection}
        currentEntityName={currentEntityName}
        selectedRelationKinds={selectedRelationKinds}
        onRelationKindToggle={handleRelationKindToggle}
        onPercentageChange={handlePercentageChange}
        searchTerm={searchTerm}
        onSearchTermChange={setSearchTerm}
        selectedEntity={selectedEntity}
        onSelectedEntityChange={setSelectedEntity}
        isEditMode={false}
        entitiesQuery={entitiesQuery}
        filteredEntities={filteredEntities}
        onCreateEntityClick={(type) => {
          setCreateEntityType(type);
          setShowCreateModal(true);
        }}
        canSubmit={!!selectedEntity && selectedRelationKinds.length > 0}
        submitButtonText={`Ajouter ${selectedRelationKinds.length} relation${selectedRelationKinds.length > 1 ? 's' : ''}`}
      />

      {/* Modal de création d'entité */}
      <AddEntityModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        entityType={createEntityType}
        onSubmit={async () => {
          // After entity creation, invalidate and refetch entities
          await queryClient.invalidateQueries({
            queryKey: ['get', '/api/organization/{organization_id}/entities'],
          });
          const result = await entitiesQuery.refetch();
          
          // Find the newly created entity by matching the search term
          if (result.data?.entities) {
            const matchingEntity = result.data.entities.find(
              (e: Entity) => 
                e.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                (e.email && e.email.toLowerCase().includes(searchTerm.toLowerCase()))
            );
            
            if (matchingEntity) {
              setSelectedEntity(matchingEntity);
              setSearchTerm(matchingEntity.name);
            }
          }
          
          setShowCreateModal(false);
        }}
        organizationId={organizationId}
      />
    </>
  );
};

const AddRelationModal: React.FC<AddRelationModalProps> = ({ 
  isOpen, 
  onClose, 
  onSubmit,
  editingRelation,
  organizationId,
  entityId,
  currentEntityName,
  relationDirection
}) => {
  if (editingRelation) {
    return (
      <EditRelationModal
        isOpen={isOpen}
        onClose={onClose}
        onSubmit={onSubmit}
        editingRelation={editingRelation}
        organizationId={organizationId}
        entityId={entityId}
        currentEntityName={currentEntityName}
        relationDirection={relationDirection}
      />
    );
  }

  return (
    <AddRelationModalComponent
      isOpen={isOpen}
      onClose={onClose}
      onSubmit={onSubmit}
      editingRelation={null}
      organizationId={organizationId}
      entityId={entityId}
      currentEntityName={currentEntityName}
      relationDirection={relationDirection}
    />
  );
};

interface EditRelationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: () => void;
  editingRelation: Relation;
  organizationId: string;
  entityId: string;
  currentEntityName: string;
  relationDirection: 'incoming' | 'outgoing';
}

const EditRelationModal: React.FC<EditRelationModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  editingRelation,
  organizationId,
  entityId,
  currentEntityName,
  relationDirection,
}) => {
  const [selectedRelationKinds, setSelectedRelationKinds] = useState<SelectedRelationKind[]>([]);
  const [selectedEntity, setSelectedEntity] = useState<Entity | null>(null);
  const [isInitialized, setIsInitialized] = useState(false);

  // When editing, fetch all relations between the two entities
  const otherEntityId = editingRelation.parent_entity_id === entityId 
    ? editingRelation.child_entity_id 
    : editingRelation.parent_entity_id;

  // Fetch all relations for the current entity (both parent and child roles) to find all relations with the other entity
  const parentRelationsQuery = $api.useQuery(
    'get',
    '/api/organization/{organization_id}/entities/{id}/relations',
    {
      params: {
        path: {
          organization_id: organizationId,
          id: entityId,
        },
        query: {
          role: 'parent',
        },
      },
    },
    {
      enabled: isOpen && !!organizationId && !!entityId && !!otherEntityId,
    }
  );

  const childRelationsQuery = $api.useQuery(
    'get',
    '/api/organization/{organization_id}/entities/{id}/relations',
    {
      params: {
        path: {
          organization_id: organizationId,
          id: entityId,
        },
        query: {
          role: 'child',
        },
      },
    },
    {
      enabled: isOpen && !!organizationId && !!entityId && !!otherEntityId,
    }
  );

  // Combine both queries' results
  const allRelations = useMemo(() => [
    ...(parentRelationsQuery.data?.relations || []),
    ...(childRelationsQuery.data?.relations || []),
  ], [parentRelationsQuery.data?.relations, childRelationsQuery.data?.relations]);
  const isLoadingRelations = parentRelationsQuery.isLoading || childRelationsQuery.isLoading;

  // Update relation mutation
  const updateRelationMutation = $api.useMutation(
    'patch',
    '/api/organization/{organization_id}/entities/{id}/relations/{relation_id}'
  );

  // Delete relation mutation
  const deleteRelationMutation = $api.useMutation(
    'delete',
    '/api/organization/{organization_id}/entities/{id}/relations/{relation_id}'
  );

  // Create relation mutation (for adding new relation kinds while editing)
  const createRelationMutation = $api.useMutation(
    'post',
    '/api/organization/{organization_id}/entities/{id}/relations'
  );

  // Initialize form when editing (only on initial load)
  useEffect(() => {
    if (isOpen && otherEntityId && !isInitialized) {
      // Wait for relations data to be loaded
      if (isLoadingRelations) {
        return;
      }

      // Filter relations that involve both entities
      const relationsBetweenEntities = allRelations.filter(rel => 
        (rel.parent_entity_id === entityId && rel.child_entity_id === otherEntityId) ||
        (rel.parent_entity_id === otherEntityId && rel.child_entity_id === entityId)
      );

      // Populate selectedRelationKinds with all existing relations (only on initial load)
      if (relationsBetweenEntities.length > 0) {
        const initialKinds: SelectedRelationKind[] = relationsBetweenEntities.map(rel => ({
          kind: rel.relation_kind,
          percentage: rel.percentage?.toString() || '',
          relationId: rel.id,
        }));

        setSelectedRelationKinds(initialKinds);
      }
      
      // Set the other entity (cannot be changed in edit mode)
      setSelectedEntity({
        id: otherEntityId,
        name: editingRelation.entity_name,
        kind: editingRelation.entity_type,
      });

      setIsInitialized(true);
    }
  }, [isOpen, entityId, otherEntityId, allRelations, isLoadingRelations, isInitialized, editingRelation.entity_name, editingRelation.entity_type]);

  // Reset initialization flag when modal closes
  useEffect(() => {
    if (!isOpen) {
      setIsInitialized(false);
      setSelectedRelationKinds([]);
      setSelectedEntity(null);
    }
  }, [isOpen]);

  const handleRelationKindToggle = (kind: RelationKind) => {
    setSelectedRelationKinds(prev => {
      const existing = prev.find(r => r.kind === kind);
      if (existing) {
        return prev.filter(r => r.kind !== kind);
      } else {
        return [...prev, { kind, percentage: '' }];
      }
    });
  };

  const handlePercentageChange = (kind: RelationKind, percentage: string) => {
    setSelectedRelationKinds(prev =>
      prev.map(r => r.kind === kind ? { ...r, percentage } : r)
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Filter relations that involve both entities
    const relationsBetweenEntities = allRelations.filter(rel => 
      (rel.parent_entity_id === entityId && rel.child_entity_id === otherEntityId) ||
      (rel.parent_entity_id === otherEntityId && rel.child_entity_id === entityId)
    );

    // If no relations are selected, delete all existing relations
    if (selectedRelationKinds.length === 0) {
      if (relationsBetweenEntities.length === 0) {
        // No relations to delete, just close
        onClose();
        return;
      }

      try {
        // Delete all relations
        for (const relationToDelete of relationsBetweenEntities) {
          await deleteRelationMutation.mutateAsync({
            params: {
              path: {
                organization_id: organizationId,
                id: relationToDelete.parent_entity_id,
                relation_id: relationToDelete.id,
              },
            },
          });
        }

        // All relations deleted successfully
        onSubmit();
        onClose();
      } catch {
        // Error is handled by the mutation's error state
      }
      return;
    }

    const parentEntityId = relationDirection === 'incoming' ? otherEntityId : entityId;
    const childEntityId = relationDirection === 'incoming' ? entityId : otherEntityId;

    try {
      // Process each selected relation kind
      for (const selectedKind of selectedRelationKinds) {
        const existingRelation = relationsBetweenEntities.find(
          rel => rel.relation_kind === selectedKind.kind
        );

        if (existingRelation) {
          // Update existing relation if percentage changed
          const percentageChanged = 
            (existingRelation.percentage?.toString() || '') !== (selectedKind.percentage || '');
          
          if (percentageChanged || existingRelation.relation_kind !== selectedKind.kind) {
            await updateRelationMutation.mutateAsync({
              params: {
                path: {
                  organization_id: organizationId,
                  id: existingRelation.parent_entity_id,
                  relation_id: existingRelation.id,
                },
              },
              body: {
                relation_kind: selectedKind.kind,
                percentage: selectedKind.percentage ? parseFloat(selectedKind.percentage) : undefined,
              },
            });
          }
        } else {
          // Create new relation
          await createRelationMutation.mutateAsync({
            params: {
              path: {
                organization_id: organizationId,
                id: parentEntityId,
              },
            },
            body: {
              child_entity_id: childEntityId,
              relation_kind: selectedKind.kind,
              percentage: selectedKind.percentage ? parseFloat(selectedKind.percentage) : undefined,
            },
          });
        }
      }

      // Delete relations that were removed
      const selectedKinds = selectedRelationKinds.map(k => k.kind);
      const relationsToDelete = relationsBetweenEntities.filter(
        rel => !selectedKinds.includes(rel.relation_kind)
      );

      for (const relationToDelete of relationsToDelete) {
        await deleteRelationMutation.mutateAsync({
          params: {
            path: {
              organization_id: organizationId,
              id: relationToDelete.parent_entity_id,
              relation_id: relationToDelete.id,
            },
          },
        });
      }

      // All operations completed successfully
      onSubmit();
      onClose();
    } catch {
      // Error is handled by the mutation's error state
    }
  };

  const isLoading = Boolean(
    updateRelationMutation.isPending || 
    deleteRelationMutation.isPending || 
    createRelationMutation.isPending ||
    isLoadingRelations
  );
  const error = updateRelationMutation.error || deleteRelationMutation.error || createRelationMutation.error;

  if (!isOpen) return null;

  return (
    <RelationModalLayout
      title="Modifier la relation"
      onClose={onClose}
      onSubmit={handleSubmit}
      isLoading={isLoading}
      error={error}
      relationDirection={relationDirection}
      currentEntityName={currentEntityName}
      selectedRelationKinds={selectedRelationKinds}
      onRelationKindToggle={handleRelationKindToggle}
      onPercentageChange={handlePercentageChange}
      searchTerm=""
      onSearchTermChange={() => {}}
      selectedEntity={selectedEntity}
      onSelectedEntityChange={() => {}}
      isEditMode={true}
      entitiesQuery={undefined}
      filteredEntities={[]}
      onCreateEntityClick={() => {}}
      canSubmit={true}
      submitButtonText={
        selectedRelationKinds.length === 0
          ? 'Supprimer toutes les relations'
          : `Modifier ${selectedRelationKinds.length} relation${selectedRelationKinds.length > 1 ? 's' : ''}`
      }
    />
  );
};

export default AddRelationModal;
